import React, { useRef, useState } from 'react';

// A game-style on-screen joystick for touch screens. Drag the knob; it reports x and y from -1 to 1
// (y is down-positive, like the screen) and springs back to the centre when you let go.
// Each stick follows only its own finger (pointer capture), so two sticks can be used at once.
const SIZE = 88;       // base diameter (px)
const KNOB = 38;       // knob diameter
const DEAD = 0.12;     // ignore tiny movements of a resting thumb

const VirtualJoystick = ({ onChange, label, className = '', style }) => {
  const baseRef = useRef();
  const pointer = useRef(null);
  const [knob, setKnob] = useState({ x: 0, y: 0, active: false });

  const update = (e) => {
    const r = baseRef.current.getBoundingClientRect();
    const max = (SIZE - KNOB) / 2;
    let dx = e.clientX - (r.left + r.width / 2), dy = e.clientY - (r.top + r.height / 2);
    const len = Math.hypot(dx, dy);
    if (len > max) { dx *= max / len; dy *= max / len; }
    setKnob({ x: dx, y: dy, active: true });
    let nx = dx / max, ny = dy / max;
    const mag = Math.hypot(nx, ny);
    if (mag < DEAD) { nx = 0; ny = 0; } else { const k = (mag - DEAD) / (1 - DEAD) / mag; nx *= k; ny *= k; }
    onChange(nx, ny);
  };
  const release = () => {
    pointer.current = null;
    setKnob({ x: 0, y: 0, active: false });
    onChange(0, 0);
  };

  return (
    <div
      ref={baseRef}
      role="application"
      aria-label={label}
      className={`fixed z-40 rounded-full select-none touch-none ${className}`}
      style={{ width: SIZE, height: SIZE, background: 'radial-gradient(circle, rgba(255,255,255,0.32), rgba(255,255,255,0.12))', border: '2px solid rgba(255,255,255,0.55)', boxShadow: '0 4px 14px rgba(0,0,0,0.25)', backdropFilter: 'blur(2px)', WebkitUserSelect: 'none', ...style }}
      onPointerDown={(e) => {
        if (pointer.current !== null) return;
        pointer.current = e.pointerId;
        e.currentTarget.setPointerCapture(e.pointerId);
        e.stopPropagation();
        update(e);
      }}
      onPointerMove={(e) => { if (e.pointerId === pointer.current) update(e); }}
      onPointerUp={(e) => { if (e.pointerId === pointer.current) release(); }}
      onPointerCancel={(e) => { if (e.pointerId === pointer.current) release(); }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        className="absolute rounded-full"
        style={{
          width: KNOB, height: KNOB, left: (SIZE - KNOB) / 2 - 2, top: (SIZE - KNOB) / 2 - 2,
          transform: `translate(${knob.x}px, ${knob.y}px)`,
          transition: knob.active ? 'none' : 'transform 0.15s ease-out',
          background: 'radial-gradient(circle at 35% 30%, #fff8e6, #d9b26a)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
        }}
      />
      <span className="absolute left-0 right-0 text-center text-[10px] font-medium text-white/90 pointer-events-none" style={{ bottom: -16, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>
        {label}
      </span>
    </div>
  );
};

export default VirtualJoystick;
